/**
 * Phase 2L-A102 — `/free-guide` — Dedicated landing page for the
 * AWARE Free Homeowner Guide ebook.
 *
 * Houses the full marketing copy, the 3D mockup, the lead-capture
 * form (Name / Email / Suburb / State), and a richer "what's inside"
 * breakdown. The homepage carries ONLY a compact teaser that links
 * here.
 *
 * Jun 2026 — repositioned from a Sunshine Coast offer to AWARE's
 * Australia-wide, mail-out-kit positioning. The Noosaville lab is
 * presented as an optional local drop-off, not the main service.
 *
 * Still noindex + NOT in sitemap while Stephen reviews the rebranded
 * page + ebook. Promotion to indexable is a separate approved slice.
 */
import { CheckCircle2, BookOpen, Download, ShieldCheck, Package, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import EbookLeadMagnet from "@/components/EbookLeadMagnet";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const WHATS_INSIDE = [
  {
    title: "Tank water essentials",
    body: "What to test for on rainwater catchments, how often, and the maintenance habits that move results the most.",
  },
  {
    title: "Bore water deep-dive",
    body: "Iron, manganese, salinity, pH, hardness — what each marker means, how to read your report, and when to consider filtration.",
  },
  {
    title: "Bacteria & coliforms",
    body: "What E. coli and coliforms actually indicate, why microbiology results are time-sensitive, and what a flagged result means in plain English.",
  },
  {
    title: "Clear water can still need testing",
    body: "Why visual checks miss most of what the Australian Drinking Water Guidelines actually target, and the seasonal moments worth catching with a quick test.",
  },
  {
    title: "Sampling done right",
    body: "How to collect, store and return a sample — by mail-out kit or local drop-off — so the result reflects your actual supply.",
  },
  {
    title: "Choosing the right next step",
    body: "Inspection vs cleaning vs filtration vs UV — when each one matters, and the conversation to have with your installer.",
  },
];

const AUDIENCE = [
  "Households on a rainwater tank, anywhere in Australia",
  "Rural, acreage and remote properties running on a bore",
  "Private water supplies with no water authority monitoring",
  "Buyers, sellers, landlords and property managers at handover",
];

export default function FreeGuide() {
  useSEO({
    title: "Free Tank & Bore Water Testing Guide Australia | AWARE Water Testing",
    description:
      "Download AWARE Water Testing's free Australian guide to tank and bore water testing. Learn what to test, when to test, and how to understand your results.",
    url: `${SEO_DEFAULTS.baseUrl}/free-guide`,
    // Jun 2026 — still noindex while Stephen reviews the rebranded
    // page + ebook. Flip to `false` (or remove) in the approved
    // promotion slice, together with the sitemap entry.
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Header />
      <main data-testid="free-guide-page">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-br from-cyan-50 via-white to-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28 pb-8 lg:pb-12">
            <div className="grid lg:grid-cols-[460px,1fr] gap-8 lg:gap-12 items-center">
              <div className="order-1 flex justify-center lg:justify-start">
                <img
                  src="/ebook/ebook-3d-cover-transparent.png"
                  alt="The Australian Homeowner's Guide to Tank & Bore Water Testing — free AWARE Water Testing ebook"
                  className="w-full max-w-[286px] sm:max-w-[358px] lg:max-w-[368px] drop-shadow-[0_28px_45px_rgba(8,47,73,0.28)]"
                  style={{ transform: "rotate(10deg)", transformOrigin: "center" }}
                  data-testid="free-guide-hero-image"
                />
              </div>
              <div className="order-2">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                  Free Homeowner Guide · Australia-wide
                </div>
                <h1
                  data-testid="free-guide-h1"
                  className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight"
                >
                  The Australian Homeowner's Guide to Tank &amp; Bore Water Testing
                </h1>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                  A free guide for Australian households using tank or bore
                  water. Plain-English answers to what to test, when to test,
                  and what your results actually mean.
                </p>
                <ul className="mt-5 space-y-2 text-slate-700">
                  {[
                    "Written for Australian tank and bore households",
                    "Reads against the Australian Drinking Water Guidelines",
                    "Mail-out sample kits available Australia-wide",
                    "Free download — no payment, no obligation",
                  ].map((b) => (
                    <li key={b} className="flex gap-2 items-start text-[15px]">
                      <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-600" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#download-form"
                  data-testid="free-guide-scroll-to-form"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3.5 text-base font-semibold transition"
                >
                  <Download className="w-4 h-4" />
                  Download the free guide
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Who it's for + why testing matters */}
        <section className="bg-slate-50 py-10 lg:py-14" data-testid="free-guide-audience">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 lg:gap-14">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                Who it's for
              </div>
              <h2 className="mt-2 font-display text-base md:text-lg font-bold text-slate-900">
                Australian homeowners relying on tank or bore water
              </h2>
              <ul className="mt-4 space-y-2 text-slate-700">
                {AUDIENCE.map((a) => (
                  <li key={a} className="flex gap-2 items-start text-[15px]">
                    <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-cyan-600" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                Why testing matters
              </div>
              <h2 className="mt-2 font-display text-base md:text-lg font-bold text-slate-900">
                Private water isn't monitored for you
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Unlike town water, a tank or bore is not continuously monitored
                by a water authority before it reaches your tap. Quality shifts
                with rainfall, dry spells, wildlife, tank condition, bore
                chemistry, nearby land use and how well filters or UV are
                maintained.
              </p>
              <p className="mt-3 text-slate-600 leading-relaxed">
                A test gives you a snapshot to work from. Where a result does
                not meet the Australian Drinking Water Guidelines, the guide
                explains what it points to and how it should be addressed.
              </p>
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section className="bg-white py-10 lg:py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                What you'll learn
              </div>
              <h2 className="mt-2 font-display text-base md:text-lg font-bold text-slate-900">
                Practical chapters, aligned to the Australian Drinking Water Guidelines
              </h2>
              <p className="mt-3 text-slate-600 leading-relaxed">
                The guide is intentionally short and useful. No fluff, no
                product pitching — just what every Australian tank or bore
                household benefits from knowing.
              </p>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {WHATS_INSIDE.map((c, i) => (
                <div
                  key={c.title}
                  data-testid={`free-guide-chapter-${i}`}
                  className="rounded-2xl bg-slate-50 ring-1 ring-slate-100 p-5"
                >
                  <div className="flex items-center gap-2 text-cyan-700">
                    <BookOpen className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wide font-bold">
                      Chapter {i + 1}
                    </span>
                  </div>
                  <h3 className="mt-2 font-display text-lg font-bold text-slate-900">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {c.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section className="bg-slate-50 py-7">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 text-cyan-700">
              <ShieldCheck className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wide">
                Written by the AWARE lab team
              </span>
            </div>
            <p className="mt-2 text-base text-slate-600 max-w-2xl mx-auto">
              The same team that processes tank and bore samples for Australian
              households every week — from coastal rainwater tanks to remote
              rural bores. Every chapter reflects what we actually see in the
              lab.
            </p>
          </div>
        </section>

        {/* Form */}
        <div id="download-form" className="scroll-mt-28">
          <EbookLeadMagnet sourceRoute="/free-guide" />
        </div>

        {/* Mail-out kit CTA — primary next step after the download */}
        <section className="bg-slate-900 py-10 lg:py-14" data-testid="free-guide-mailout-band">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 text-cyan-300">
              <Package className="w-5 h-5" />
              <span className="text-sm font-semibold uppercase tracking-wide">
                Mail-out kits · Australia-wide
              </span>
            </div>
            <h2 className="mt-3 font-display text-base md:text-lg font-bold text-white">
              Ready to test your water?
            </h2>
            <p className="mt-3 text-slate-300 max-w-2xl mx-auto leading-relaxed">
              We post sample kits anywhere in Australia with a prepaid return
              label included — collect your sample at home and send it straight
              back to the lab. Customers near us can also drop a sample in to
              our Noosaville lab.
            </p>
            <Link
              to="/book-water-test?kit=mail_out"
              data-testid="free-guide-mailout-cta"
              className="group mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 px-7 py-3.5 text-base font-bold transition"
            >
              Order a mail-out water testing kit
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </section>

        {/* Next steps */}
        <section className="bg-white py-10 lg:py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-base md:text-lg font-bold text-slate-900">
              Not sure which test you need?
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Compare the AWARE packages, or book straight in — both are a
              click away.
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <Link
                to="/#water-tests"
                data-testid="free-guide-next-compare"
                className="inline-flex items-center rounded-full border border-slate-300 bg-white hover:border-cyan-400 hover:text-cyan-700 px-6 py-3 text-sm font-semibold text-slate-700 transition"
              >
                Compare AWARE test packages
              </Link>
              <Link
                to="/book-water-test"
                data-testid="free-guide-next-book"
                className="inline-flex items-center rounded-full bg-cyan-600 hover:bg-cyan-700 text-white px-6 py-3 text-sm font-semibold transition"
              >
                Book online — one flat all-inclusive price
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
